import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { KeyRound, Copy, Check, ShieldAlert, RefreshCw, Search, History } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const STAFF_ROLES = [
  'sales_manager', 'sales_team_leader', 'sales_agent',
  'marketing_manager', 'marketing_team_leader', 'marketing_agent',
  'it_manager', 'it_staff',
  'accounting_manager', 'accounting_staff',
  'hr_manager', 'hr_staff',
  'quality_control_lead', 'quality_control_agent',
  'ad_operations_lead', 'ad_operations_agent',
];

const MANAGER_ROLES = new Set([
  'sales_manager', 'marketing_manager', 'it_manager',
  'accounting_manager', 'hr_manager',
  'quality_control_lead', 'ad_operations_lead', 'sales_team_leader', 'marketing_team_leader',
]);

type StaffUser = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  role: string;
  created_at: string;
  last_rotated_at: string | null;
  last_rotated_by_name: string | null;
  last_rotated_by_role: string | null;
  rotation_count: number;
};

type RotationRow = {
  id: string;
  rotated_at: string;
  actor_id: string | null;
  actor_role: string | null;
  actor_name: string | null;
  target_roles: string[];
};

const prettyRole = (r: string) => r.replace(/_/g, ' ');

export default function StaffCredentials() {
  const { hasRole } = useAuth();
  const canRotate = hasRole('superadmin') || hasRole('it_manager') || hasRole('it_staff');

  const [confirm, setConfirm] = useState<StaffUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ email: string; password: string; rotated_at: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [historyFor, setHistoryFor] = useState<StaffUser | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['staff-users-credentials'],
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('user_id, role, created_at')
        .in('role', STAFF_ROLES as any);
      if (error) throw error;
      const ids = [...new Set((roles || []).map((r: any) => r.user_id))];
      if (!ids.length) return [] as StaffUser[];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, email, phone')
        .in('user_id', ids);
      const pMap = new Map<string, any>();
      (profiles || []).forEach((p: any) => pMap.set(p.user_id, p));

      // Last rotation per user (+ counts + actor name)
      const { data: rots } = await supabase
        .from('staff_password_rotations' as any)
        .select('target_user_id, rotated_at, actor_id, actor_role')
        .in('target_user_id', ids)
        .order('rotated_at', { ascending: false });
      const lastByUser = new Map<string, { rotated_at: string; actor_id: string | null; actor_role: string | null }>();
      const countByUser = new Map<string, number>();
      const actorIds = new Set<string>();
      (rots || []).forEach((r: any) => {
        countByUser.set(r.target_user_id, (countByUser.get(r.target_user_id) || 0) + 1);
        if (!lastByUser.has(r.target_user_id)) {
          lastByUser.set(r.target_user_id, {
            rotated_at: r.rotated_at, actor_id: r.actor_id, actor_role: r.actor_role,
          });
        }
        if (r.actor_id) actorIds.add(r.actor_id);
      });
      const actorMap = new Map<string, string>();
      if (actorIds.size) {
        const { data: actors } = await supabase
          .from('profiles')
          .select('user_id, full_name, email')
          .in('user_id', [...actorIds]);
        (actors || []).forEach((a: any) =>
          actorMap.set(a.user_id, a.full_name || a.email || a.user_id.slice(0, 8)),
        );
      }

      return (roles as any[]).map((r) => {
        const p = pMap.get(r.user_id) || {};
        const last = lastByUser.get(r.user_id) || null;
        return {
          user_id: r.user_id,
          role: r.role,
          created_at: r.created_at,
          full_name: p.full_name || null,
          email: p.email || null,
          phone: p.phone || null,
          last_rotated_at: last?.rotated_at || null,
          last_rotated_by_name: last?.actor_id ? (actorMap.get(last.actor_id) || null) : null,
          last_rotated_by_role: last?.actor_role || null,
          rotation_count: countByUser.get(r.user_id) || 0,
        } as StaffUser;
      }).sort((a, b) =>
        a.role.localeCompare(b.role) ||
        (a.full_name || '').localeCompare(b.full_name || ''),
      );
    },
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['staff-pw-rotations', historyFor?.user_id],
    enabled: !!historyFor,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('staff_password_rotations' as any)
        .select('id, rotated_at, actor_id, actor_role, target_roles')
        .eq('target_user_id', historyFor!.user_id)
        .order('rotated_at', { ascending: false });
      if (error) throw error;
      const ids = [...new Set((rows || []).map((r: any) => r.actor_id).filter(Boolean))] as string[];
      const nameMap = new Map<string, string>();
      if (ids.length) {
        const { data: actors } = await supabase
          .from('profiles')
          .select('user_id, full_name, email')
          .in('user_id', ids);
        (actors || []).forEach((a: any) =>
          nameMap.set(a.user_id, a.full_name || a.email || a.user_id.slice(0, 8)),
        );
      }
      return (rows || []).map((r: any) => ({
        id: r.id,
        rotated_at: r.rotated_at,
        actor_id: r.actor_id,
        actor_role: r.actor_role,
        actor_name: r.actor_id ? (nameMap.get(r.actor_id) || null) : null,
        target_roles: r.target_roles || [],
      } as RotationRow));
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data || []).filter((u) => {
      if (filter === 'managers' && !MANAGER_ROLES.has(u.role)) return false;
      if (filter !== 'all' && filter !== 'managers' && u.role !== filter) return false;
      if (!q) return true;
      return (
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q)
      );
    });
  }, [data, search, filter]);

  const rotate = async () => {
    if (!confirm) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke('rotate-staff-password', {
      body: { user_id: confirm.user_id },
    });
    setBusy(false);
    if (error || (data as any)?.error) {
      toast.error((error?.message || (data as any)?.error) || 'Failed to rotate password');
      return;
    }
    setResult({
      email: (data as any).email,
      password: (data as any).password,
      rotated_at: (data as any).rotated_at,
    });
    setConfirm(null);
    toast.success('Password rotated. Share new credentials securely.');
  };

  const copyCreds = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(`Email: ${result.email}\nPassword: ${result.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <KeyRound className="h-6 w-6 text-primary" /> Staff Credentials
        </h1>
        <p className="text-sm text-muted-foreground">
          Rotate and reset passwords for Department Managers and all Internal Staff. A new strong password is generated each time.
          {!canRotate && (
            <span className="block mt-1 text-amber-600">
              You do not have permission to rotate passwords. Only Superadmin, IT Manager, and IT Staff can rotate.
            </span>
          )}
        </p>
      </div>

      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="p-4 flex gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 dark:text-amber-200">
            Rotating a password immediately invalidates the user's previous password and signs them out of new sessions.
            The new password is shown <strong>only once</strong> — copy and share it through a secure channel.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">Internal staff</CardTitle>
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, phone, role"
                className="pl-7 h-8 text-xs w-[240px]"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-8 w-[200px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All staff</SelectItem>
                <SelectItem value="managers">Department Managers only</SelectItem>
                {STAFF_ROLES.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">{prettyRole(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-48 m-4" />
          ) : !filtered.length ? (
            <p className="text-sm text-muted-foreground text-center py-12">No matching staff found.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Last rotated</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u) => (
                    <TableRow key={`${u.user_id}-${u.role}`}>
                      <TableCell className="text-xs font-medium">{u.full_name || '—'}</TableCell>
                      <TableCell className="text-xs">{u.email || '—'}</TableCell>
                      <TableCell className="text-xs">{u.phone || '—'}</TableCell>
                      <TableCell>
                        <Badge
                          variant={MANAGER_ROLES.has(u.role) ? 'default' : 'outline'}
                          className="text-[10px] capitalize"
                        >
                          {prettyRole(u.role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {u.last_rotated_at ? (
                          <div className="space-y-0.5">
                            <div title={format(new Date(u.last_rotated_at), 'd MMM yyyy, HH:mm')}>
                              {formatDistanceToNow(new Date(u.last_rotated_at), { addSuffix: true })}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              by {u.last_rotated_by_name || 'system'}
                              {u.last_rotated_by_role && (
                                <span className="capitalize"> ({prettyRole(u.last_rotated_by_role)})</span>
                              )}
                              {u.rotation_count > 1 && <> · {u.rotation_count}×</>}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Never</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1"
                            disabled={!u.rotation_count}
                            onClick={() => setHistoryFor(u)}
                          >
                            <History className="h-3.5 w-3.5" /> History
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            disabled={!canRotate}
                            onClick={() => setConfirm(u)}
                          >
                            <RefreshCw className="h-3.5 w-3.5" /> Rotate
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate password?</AlertDialogTitle>
            <AlertDialogDescription>
              A new strong password will be generated for{' '}
              <strong>{confirm?.full_name || confirm?.email}</strong>
              {confirm?.role && <> (<span className="capitalize">{prettyRole(confirm.role)}</span>)</>}.
              Their existing password will stop working immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={rotate} disabled={busy}>
              {busy ? 'Rotating…' : 'Yes, rotate password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!result} onOpenChange={(o) => !o && (setResult(null), refetch())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> New credentials
            </DialogTitle>
            <DialogDescription>
              Copy and share securely. This password will not be shown again.
              {result && <span className="block mt-1 text-[11px] text-muted-foreground">Rotated {format(new Date(result.rotated_at), 'd MMM yyyy, HH:mm')}</span>}
            </DialogDescription>
          </DialogHeader>
          {result && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Email</label>
                <Input readOnly value={result.email} className="font-mono text-xs" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">New password</label>
                <Input readOnly value={result.password} className="font-mono text-sm" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={copyCreds} className="gap-1">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy email & password'}
            </Button>
            <Button onClick={() => { setResult(null); refetch(); }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotation history */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" /> Rotation history
            </DialogTitle>
            <DialogDescription>
              Password rotations for <strong>{historyFor?.full_name || historyFor?.email}</strong>
              {historyFor?.role && <> (<span className="capitalize">{prettyRole(historyFor.role)}</span>)</>}.
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <Skeleton className="h-40" />
          ) : !history?.length ? (
            <p className="text-sm text-muted-foreground text-center py-8">No rotations recorded yet.</p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Caller role</TableHead>
                    <TableHead>Roles at time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="text-xs">
                        <div>{format(new Date(h.rotated_at), 'd MMM yyyy, HH:mm')}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(h.rotated_at), { addSuffix: true })}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{h.actor_name || '—'}</TableCell>
                      <TableCell className="text-xs capitalize">
                        {h.actor_role ? prettyRole(h.actor_role) : '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-wrap gap-1">
                          {(h.target_roles || []).map((r) => (
                            <Badge key={r} variant="outline" className="text-[10px] capitalize">
                              {prettyRole(r)}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setHistoryFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
